import { digitsOnly, normalizeUkDigits } from './phone';

export type ContactKind = 'person' | 'user' | 'hunt_group' | 'system';
export type ContactSource = 'local' | 'elevate' | 'univerge';
export type ContactSyncStatus = 'pending' | 'synced' | 'failed' | 'conflict' | 'blocked';
export type ContactStatus = 'staged' | 'promoted';

export type JarvisContact = {
  id: string;
  elevateContactId: string | null;
  source: ContactSource;
  kind: ContactKind;
  status: ContactStatus;
  customerId: string | null;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  email: string | null;
  phoneOriginal: string | null;
  phoneNormalized: string | null;
  landlineOriginal: string | null;
  landlineNormalized: string | null;
  providerExternalContactId: string | null;
  duplicateHints: string[];
  localEditedAt: string | null;
  deletedAt: string | null;
  lastImportedAt: string | null;
  lastSyncedAt: string | null;
  syncStatus: ContactSyncStatus;
  pendingChanges: Record<string, unknown>;
  lastSyncError: string | null;
  internalNotes: string | null;
  syncIdempotencyKey: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ContactDraft = {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  landline: string;
  internalNotes: string;
  displayName?: string;
};

export type SharedContactFields = {
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  email: string | null;
  phoneOriginal: string | null;
  landlineOriginal: string | null;
};

export type RecordingContactLink = {
  ownerUserId: string;
  recordingId: string;
  contactId: string | null;
};

export function classifyElevateContact(raw: Record<string, unknown>): ContactKind {
  const type = String(raw.type || raw.contactType || raw.kind || '').toLowerCase();
  if (type.includes('hunt') || (type.includes('group') && !type.includes('user'))) return 'hunt_group';
  if (type === 'user' || type.endsWith('.user')) return 'user';
  if (
    type.includes('chatbot')
    || type.includes('hpbx')
    || type.includes('resource')
    || type.includes('autoattendant')
    || type.includes('ivr')
    || type.includes('queue')
  ) return 'system';
  if (
    type.includes('external')
    || type.includes('private')
    || type === 'person'
    || type === 'contact'
    || type === 'customer'
  ) return 'person';
  return 'system';
}

export function isImportablePerson(kind: ContactKind): boolean {
  return kind === 'person';
}

function readText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export function readElevateEmail(raw: Record<string, unknown>): string | null {
  if (typeof raw.email === 'string') return raw.email.trim() || null;
  if (raw.email && typeof raw.email === 'object') {
    const record = raw.email as Record<string, unknown>;
    return readText(record.address || record.email || record.value || record.emailAddress);
  }
  return readText(raw.emailAddress || raw.email_address);
}

export function readElevatePhones(raw: Record<string, unknown>): Array<{ original: string; type: string }> {
  const list = Array.isArray(raw.phoneNumbers) ? raw.phoneNumbers : [];
  return list.map((item) => {
    if (typeof item === 'string') return { original: item, type: '' };
    if (!item || typeof item !== 'object') return { original: '', type: '' };
    const record = item as Record<string, unknown>;
    const original = readText(record.number || record.internationalFormatNumber || record.phoneNumber || record.phone) || '';
    return { original, type: String(record.type || '').toLowerCase() };
  }).filter((item) => item.original);
}

export function pickPhones(raw: Record<string, unknown>): { phoneOriginal: string | null; landlineOriginal: string | null } {
  const phones = readElevatePhones(raw);
  const mobile = phones.find((item) => item.type.includes('mobile') || item.type.includes('cell'));
  const landline = phones.find((item) => item.type.includes('landline') || item.type.includes('work') || item.type.includes('home') || item.type.includes('office'));
  const first = phones[0] || null;
  const second = phones[1] || null;
  return {
    phoneOriginal: mobile?.original || first?.original || null,
    landlineOriginal: landline?.original || (mobile ? first?.original === mobile.original ? second?.original || null : first?.original || null : second?.original || null),
  };
}

export function contactDisplayNameFromParts(input: {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
  company?: string | null;
}): string {
  const combined = `${input.firstName || ''} ${input.lastName || ''}`.trim();
  return combined || String(input.displayName || '').trim() || String(input.email || '').trim() || String(input.company || '').trim() || 'Unnamed contact';
}

export function mapElevateContact(raw: Record<string, unknown>): SharedContactFields & { elevateContactId: string } | null {
  const kind = classifyElevateContact(raw);
  if (!isImportablePerson(kind)) return null;
  const elevateContactId = readText(raw.id);
  if (!elevateContactId) return null;
  const firstName = readText(raw.firstName || raw.first_name || raw.phoneticFirstName);
  const lastName = readText(raw.lastName || raw.last_name || raw.phoneticLastName);
  const email = readElevateEmail(raw);
  const company = readText(raw.department || raw.company || raw.organization || (typeof raw.location === 'string' ? raw.location : null));
  const phones = pickPhones(raw);
  return {
    elevateContactId,
    displayName: contactDisplayNameFromParts({
      firstName,
      lastName,
      displayName: readText(raw.displayName || raw.display_name || raw.name || raw.fullName),
      email,
      company,
    }),
    firstName,
    lastName,
    company,
    email,
    phoneOriginal: phones.phoneOriginal,
    landlineOriginal: phones.landlineOriginal,
  };
}

export function shouldPreserveLocalEdits(existing: { localEditedAt?: string | null; deletedAt?: string | null }): 'deleted' | 'edited' | 'replace' {
  if (existing.deletedAt) return 'deleted';
  if (existing.localEditedAt) return 'edited';
  return 'replace';
}

export function suggestedNameFromCaller(original: string | null | undefined): { original: string | null; firstName: string; lastName: string; displayName: string } {
  const preserved = String(original || '').trim() || null;
  if (!preserved) return { original: null, firstName: '', lastName: '', displayName: '' };
  const parts = preserved.split(/\s+/);
  return {
    original: preserved,
    firstName: parts[0] || '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
    displayName: preserved,
  };
}

export function validateStagedIdentityDraft(draft: ContactDraft): string | null {
  const displayName = String(draft.displayName || '').trim() || `${draft.firstName.trim()} ${draft.lastName.trim()}`.trim();
  const phone = draft.phone.trim() || draft.landline.trim();
  if (!displayName) return 'Enter a name so this caller can be identified.';
  if (!phone) return 'A telephone number is required.';
  if (digitsOnly(phone).length < 7) return 'Enter a valid telephone number.';
  if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
    return 'Enter a valid email address.';
  }
  return null;
}

export function validateContactDraft(draft: ContactDraft): string | null {
  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  const company = draft.company.trim();
  const email = draft.email.trim();
  const phone = draft.phone.trim();
  const landline = draft.landline.trim();
  if (!firstName && !lastName && !company && !email && !phone && !landline && !String(draft.displayName || '').trim()) {
    return 'Enter a name, company, email or telephone number.';
  }
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Enter a valid email address.';
  }
  if (phone && digitsOnly(phone).length < 7) {
    return 'Enter a valid telephone number.';
  }
  if (landline && digitsOnly(landline).length < 7) {
    return 'Enter a valid landline number.';
  }
  return null;
}

export function draftToContactPatch(draft: ContactDraft): {
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  email: string | null;
  phone_original: string | null;
  phone_normalized: string | null;
  landline_original: string | null;
  landline_normalized: string | null;
  internal_notes: string | null;
} {
  const firstName = draft.firstName.trim() || null;
  const lastName = draft.lastName.trim() || null;
  const company = draft.company.trim() || null;
  const email = draft.email.trim() || null;
  const phone = draft.phone.trim() || null;
  const landline = draft.landline.trim() || null;
  const displayName = String(draft.displayName || '').trim() || contactDisplayNameFromParts({ firstName, lastName, email, company });
  return {
    display_name: displayName,
    first_name: firstName,
    last_name: lastName,
    company,
    email,
    phone_original: phone,
    phone_normalized: normalizeUkDigits(phone),
    landline_original: landline,
    landline_normalized: normalizeUkDigits(landline),
    internal_notes: draft.internalNotes.trim() || null,
  };
}

export function sharedFieldsFromDraft(draft: ContactDraft): SharedContactFields {
  const patch = draftToContactPatch(draft);
  return {
    displayName: patch.display_name,
    firstName: patch.first_name,
    lastName: patch.last_name,
    company: patch.company,
    email: patch.email,
    phoneOriginal: patch.phone_original,
    landlineOriginal: patch.landline_original,
  };
}

export function hostpilotPayloadFromShared(fields: SharedContactFields): {
  DisplayName: string;
  FirstName: string | null;
  LastName: string | null;
  Company: string | null;
  EmailAddress: string | null;
  MobilePhone: string | null;
  BusinessPhone: string | null;
} {
  return {
    DisplayName: fields.displayName.slice(0, 256),
    FirstName: fields.firstName,
    LastName: fields.lastName,
    Company: fields.company,
    EmailAddress: fields.email,
    MobilePhone: fields.phoneOriginal,
    BusinessPhone: fields.landlineOriginal || fields.phoneOriginal,
  };
}

export function detectSharedFieldConflict(
  local: SharedContactFields,
  incoming: SharedContactFields,
  lastSnapshot: SharedContactFields | null,
  localEditedAt: string | null,
  lastSyncedAt: string | null,
): boolean {
  if (!localEditedAt || !lastSyncedAt || !lastSnapshot) return false;
  if (Date.parse(localEditedAt) <= Date.parse(lastSyncedAt)) return false;
  const keys: Array<keyof SharedContactFields> = ['displayName', 'firstName', 'lastName', 'company', 'email', 'phoneOriginal', 'landlineOriginal'];
  const localChanged = keys.some((key) => (local[key] || null) !== (lastSnapshot[key] || null));
  const providerChanged = keys.some((key) => (incoming[key] || null) !== (lastSnapshot[key] || null));
  return localChanged && providerChanged;
}

export function mapHostpilotContact(raw: Record<string, unknown>): SharedContactFields & { providerExternalContactId: string } | null {
  const providerExternalContactId = readText(raw.GUID || raw.Guid || raw.Identity || raw.id);
  if (!providerExternalContactId) return null;
  const firstName = readText(raw.FirstName || raw.firstName);
  const lastName = readText(raw.LastName || raw.lastName);
  const email = readText(raw.EmailAddress || raw.email);
  const company = readText(raw.Company || raw.company);
  return {
    providerExternalContactId,
    displayName: contactDisplayNameFromParts({
      firstName,
      lastName,
      displayName: readText(raw.DisplayName || raw.displayName || raw.Name),
      email,
      company,
    }),
    firstName,
    lastName,
    company,
    email,
    phoneOriginal: readText(raw.MobilePhone || raw.mobilePhone),
    landlineOriginal: readText(raw.BusinessPhone || raw.HomePhone || raw.businessPhone),
  };
}

export function matchesContactSearch(contact: JarvisContact, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    contact.displayName,
    contact.firstName,
    contact.lastName,
    contact.company,
    contact.email,
    contact.phoneOriginal,
    contact.phoneNormalized,
    contact.landlineOriginal,
    contact.landlineNormalized,
  ].filter(Boolean).join(' ').toLowerCase();
  const compact = hay.replace(/\D/g, '');
  const needleDigits = needle.replace(/\D/g, '');
  return hay.includes(needle) || (!!needleDigits && compact.includes(needleDigits));
}

export function findDuplicateHints(
  contact: Pick<JarvisContact, 'id' | 'email' | 'phoneNormalized' | 'landlineNormalized' | 'displayName'>,
  others: Array<Pick<JarvisContact, 'id' | 'email' | 'phoneNormalized' | 'landlineNormalized' | 'displayName' | 'deletedAt'>>,
): string[] {
  const hints: string[] = [];
  const phones = [contact.phoneNormalized, contact.landlineNormalized].filter(Boolean) as string[];
  const email = contact.email?.trim().toLowerCase() || '';

  others.forEach((other) => {
    if (other.id === contact.id || other.deletedAt) return;
    const otherPhones = [other.phoneNormalized, other.landlineNormalized].filter(Boolean);
    if (email && other.email && other.email.trim().toLowerCase() === email) {
      hints.push(`Same email as ${other.displayName}`);
    }
    if (phones.some((phone) => otherPhones.includes(phone))) {
      hints.push(`Same number as ${other.displayName}`);
    }
  });

  return [...new Set(hints)];
}

export function findContactsByNormalizedPhone(
  contacts: JarvisContact[],
  phone: string | null,
): JarvisContact[] {
  const needle = normalizeUkDigits(phone);
  if (!needle) return [];
  return contacts.filter((contact) => !contact.deletedAt && (
    contact.phoneNormalized === needle || contact.landlineNormalized === needle
  ));
}

function asSyncStatus(value: unknown): ContactSyncStatus {
  if (value === 'synced' || value === 'failed' || value === 'conflict' || value === 'blocked' || value === 'pending') return value;
  return 'pending';
}

function asSource(value: unknown): ContactSource {
  if (value === 'elevate' || value === 'univerge') return value;
  return 'local';
}

function asContactStatus(value: unknown): ContactStatus {
  if (value === 'promoted') return 'promoted';
  return 'staged';
}

export function rowToContact(row: Record<string, unknown>): JarvisContact {
  return {
    id: String(row.id),
    elevateContactId: row.elevate_contact_id ? String(row.elevate_contact_id) : null,
    source: asSource(row.source),
    kind: (row.kind as ContactKind) || 'person',
    status: asContactStatus(row.status),
    customerId: row.customer_id ? String(row.customer_id) : null,
    displayName: String(row.display_name || 'Unnamed contact'),
    firstName: row.first_name ? String(row.first_name) : null,
    lastName: row.last_name ? String(row.last_name) : null,
    company: row.company ? String(row.company) : null,
    email: row.email ? String(row.email) : null,
    phoneOriginal: row.phone_original ? String(row.phone_original) : null,
    phoneNormalized: row.phone_normalized ? String(row.phone_normalized) : null,
    landlineOriginal: row.landline_original ? String(row.landline_original) : null,
    landlineNormalized: row.landline_normalized ? String(row.landline_normalized) : null,
    providerExternalContactId: row.provider_external_contact_id ? String(row.provider_external_contact_id) : null,
    duplicateHints: Array.isArray(row.duplicate_hints) ? row.duplicate_hints.map(String) : [],
    localEditedAt: row.local_edited_at ? String(row.local_edited_at) : null,
    deletedAt: row.deleted_at ? String(row.deleted_at) : null,
    lastImportedAt: row.last_imported_at ? String(row.last_imported_at) : null,
    lastSyncedAt: row.last_synced_at ? String(row.last_synced_at) : null,
    syncStatus: asSyncStatus(row.sync_status),
    pendingChanges: row.pending_changes && typeof row.pending_changes === 'object' && !Array.isArray(row.pending_changes)
      ? row.pending_changes as Record<string, unknown>
      : {},
    lastSyncError: row.last_sync_error ? String(row.last_sync_error) : null,
    internalNotes: row.internal_notes ? String(row.internal_notes) : null,
    syncIdempotencyKey: row.sync_idempotency_key ? String(row.sync_idempotency_key) : null,
    createdAt: String(row.created_at || ''),
    updatedAt: String(row.updated_at || ''),
  };
}
